import { useCallback, useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { fmtMoney, balanceClass, todayStr, thisMonth } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { FileText, Plant, PencilSimple, Trash, Eraser } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PrintableStatement } from "@/components/PrintableStatement";
import { ConfirmDelete } from "@/components/ConfirmDelete";

// One shared "labourer detail" panel, opened by clicking a name from
// anywhere in the app (Labourers list, Attendance day/month view). Holds
// everything that used to be spread across table columns and row icons --
// both balances, and the Statement/Arecanut Advances/Edit/Delete actions.
export function EmployeeDetail({ empId, onClose, onChanged }) {
  const [employee, setEmployee] = useState(null);
  const [balance, setBalance] = useState({ regular: 0, arecanut: 0 });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", gender: "M", opening_balance: 0 });
  const [stmtOpen, setStmtOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);

  const load = useCallback(() => {
    if (!empId) return;
    api.get("/employees").then((r) => setEmployee(r.data.find((e) => e.id === empId) || null));
    api.get("/balances").then((r) => setBalance(r.data[empId] || { regular: 0, arecanut: 0 }));
  }, [empId]);
  useEffect(() => { load(); }, [load]);

  // A sub-dialog (Statement / Arecanut Advances) can change this labourer's
  // balances -- refresh both this panel and whatever list opened it once it closes.
  const closeSub = (setter) => (v) => {
    setter(v);
    if (!v) { load(); onChanged?.(); }
  };

  const openEdit = () => {
    if (!employee) return;
    setEditForm({ name: employee.name, gender: employee.gender, opening_balance: employee.opening_balance });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error("Name is required"); return; }
    try {
      await api.put(`/employees/${empId}`, { ...editForm, opening_balance: Number(editForm.opening_balance) || 0 });
      toast.success("Labourer updated");
      setEditOpen(false);
      load();
      onChanged?.();
    } catch { toast.error("Save failed"); }
  };

  const remove = async () => {
    await api.delete(`/employees/${empId}`);
    toast.success("Labourer removed");
    onChanged?.();
    onClose();
  };

  return (
    <>
      {/* Actually closed (not just covered) while the Statement flow is
          active. PrintableStatement renders a plain full-screen overlay,
          not a Radix Dialog -- a Sheet left "open" underneath keeps its
          focus trap, outside-dismiss, and scroll lock all active, and each
          one independently fights clicks/scroll/focus inside that overlay
          (suppressing just the dismiss triggers wasn't enough -- the focus
          trap alone kept stealing focus back into the hidden Sheet on every
          click). Genuinely closing it turns off all three at once. The
          onPointerDownOutside/onInteractOutside guards stay as a defensive
          backstop for the brief window while its close animation is still
          in flight. */}
      <Sheet open={!!empId && !stmtOpen} onOpenChange={(o) => !o && onClose()}>
        <SheetContent
          className="w-full sm:max-w-md overflow-y-auto"
          data-testid="employee-detail"
          onPointerDownOutside={(e) => { if (e.target.closest?.("[data-printable-overlay]")) e.preventDefault(); }}
          onInteractOutside={(e) => { if (e.target.closest?.("[data-printable-overlay]")) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (document.querySelector("[data-printable-overlay]")) e.preventDefault(); }}
        >
          <SheetHeader>
            <SheetTitle className="text-xl">{employee?.name || "Labourer"}</SheetTitle>
          </SheetHeader>
          {employee && (
            <div className="mt-4 space-y-5">
              <p className="text-sm text-slate-500">
                {employee.gender === "M" ? "Male" : "Female"}
                {employee.recent_days_worked > 0 ? ` · ${employee.recent_days_worked} days worked in the last 30 days` : ""}
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Regular Balance</div>
                  <div className={`font-data text-lg font-semibold ${balanceClass(balance.regular)}`} data-testid="detail-balance-regular">{fmtMoney(balance.regular)}</div>
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Arecanut Balance</div>
                  <div className={`font-data text-lg font-semibold ${balanceClass(balance.arecanut)}`} data-testid="detail-balance-arecanut">{fmtMoney(balance.arecanut)}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="detail-btn-statement" variant="outline" className="gap-2 justify-start" onClick={() => setStmtOpen(true)}>
                  <FileText size={16} /> Statement
                </Button>
                <Button data-testid="detail-btn-arecanut" variant="outline" className="gap-2 justify-start text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => setAdvOpen(true)}>
                  <Plant size={16} /> Arecanut Advances
                </Button>
                <Button data-testid="detail-btn-clear" variant="outline" className="gap-2 justify-start col-span-2 text-amber-700 border-amber-200 hover:bg-amber-50"
                  disabled={!balance.regular && !balance.arecanut} onClick={() => setClearOpen(true)}>
                  <Eraser size={16} /> Clear Balance (settle account)
                </Button>
                <Button data-testid="detail-btn-edit" variant="outline" className="gap-2 justify-start" onClick={openEdit}>
                  <PencilSimple size={16} /> Edit
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="detail-btn-delete" variant="outline" className="gap-2 justify-start text-rose-600 border-rose-200 hover:bg-rose-50">
                      <Trash size={16} /> Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {employee.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This removes the labourer along with their attendance and payment records. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="detail-confirm-delete" className="bg-rose-600 hover:bg-rose-700" onClick={remove}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Edit Labourer</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="detail-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Gender</Label>
              <Select value={editForm.gender} onValueChange={(v) => setEditForm({ ...editForm, gender: v })}>
                <SelectTrigger data-testid="detail-edit-gender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Male</SelectItem>
                  <SelectItem value="F">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Opening Balance (₹)</Label>
              <Input data-testid="detail-edit-opening" type="number" value={editForm.opening_balance} onChange={(e) => setEditForm({ ...editForm, opening_balance: e.target.value })} />
              <p className="text-xs text-slate-500">Use a negative value if the labourer owes you money.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button data-testid="detail-btn-save-edit" className="bg-slate-900 hover:bg-slate-800" onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LabourStatement empId={empId} open={stmtOpen} onOpenChange={closeSub(setStmtOpen)} />
      <ArecanutAdvances empId={empId} empName={employee?.name} open={advOpen} onOpenChange={closeSub(setAdvOpen)} />
      <ClearBalanceDialog empId={empId} empName={employee?.name} open={clearOpen} onOpenChange={closeSub(setClearOpen)} />
    </>
  );
}

function ArecanutAdvances({ empId, empName, open, onOpenChange }) {
  const [advances, setAdvances] = useState([]);
  const [balance, setBalance] = useState(0);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayStr());
  const [note, setNote] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [clearOpen, setClearOpen] = useState(false);

  const load = useCallback(() => {
    if (!empId) return;
    api.get(`/employees/${empId}/arecanut-advances`).then((r) => setAdvances(r.data));
    api.get("/balances").then((r) => setBalance(r.data[empId]?.arecanut || 0));
  }, [empId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const add = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post(`/employees/${empId}/arecanut-advances`, { amount: Number(amount), date, note });
      toast.success("Advance recorded");
      setAmount(""); setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/arecanut-advances/${id}`);
    toast.success("Advance removed");
    load();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader><DialogTitle>Arecanut Advances — {empName}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="rounded-md bg-slate-900 text-white p-4 flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-slate-400">Arecanut Balance</span>
            <span className={`font-data text-lg font-semibold ${balance < 0 ? "text-rose-400" : "text-emerald-400"}`} data-testid="arecanut-dialog-balance">{fmtMoney(balance)}</span>
          </div>
          {Math.abs(balance) >= 0.01 && (
            <Button data-testid="btn-clear-arecanut" variant="outline" className="w-full gap-2 text-amber-700 border-amber-200 hover:bg-amber-50"
              onClick={() => setClearOpen(true)}>
              <Eraser size={16} /> Clear Arecanut Balance
            </Button>
          )}

          <div className="grid grid-cols-3 gap-2 items-end">
            <div className="space-y-1.5 col-span-1">
              <Label className="text-xs">Amount (₹)</Label>
              <Input data-testid="arecanut-adv-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label className="text-xs">Date</Label>
              <Input data-testid="arecanut-adv-date" type="date" className="font-data" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <Button data-testid="btn-add-arecanut-advance" onClick={add} className="bg-emerald-600 hover:bg-emerald-700">Add</Button>
          </div>
          <Input data-testid="arecanut-adv-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" />

          <div className="border border-slate-200 rounded-md overflow-hidden max-h-56 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500 sticky top-0">
                  <th className="text-left px-3 py-2 font-semibold">Date</th>
                  <th className="text-right px-3 py-2 font-semibold">Amount</th>
                  <th className="text-left px-3 py-2 font-semibold">Note</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {advances.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-data text-slate-600">{a.date}</td>
                    <td className="px-3 py-2 text-right font-data">{fmtMoney(a.amount)}</td>
                    <td className="px-3 py-2 text-slate-500">{a.note}</td>
                    <td className="px-3 py-2 text-right">
                      <button data-testid={`delete-arecanut-adv-${a.id}`} aria-label="Delete advance" onClick={() => setConfirmDelete({
                        title: "Delete this arecanut advance?",
                        description: `${fmtMoney(a.amount)} given on ${a.date}${a.note ? ` (${a.note})` : ""}. This cannot be undone.`,
                        onConfirm: () => remove(a.id),
                      })}
                        className="h-7 w-7 flex items-center justify-center rounded-md text-rose-500 hover:bg-rose-50 ml-auto"><Trash size={14} /></button>
                    </td>
                  </tr>
                ))}
                {advances.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">No advances given. Arecanut earnings will add up on their own.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
        <ConfirmDelete target={confirmDelete} onClose={() => setConfirmDelete(null)} />
        <ClearBalanceDialog empId={empId} empName={empName} only="arecanut" open={clearOpen}
          onOpenChange={(v) => { setClearOpen(v); if (!v) load(); }} />
      </DialogContent>
    </Dialog>
  );
}

const evtStyle = {
  shift: "text-emerald-700",
  lend: "text-orange-600",
  advance: "text-orange-600",
  payment: "text-sky-700",
  absent: "text-rose-500",
};

function LabourStatement({ empId, open, onOpenChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [range, setRange] = useState({ start: `${thisMonth()}-01`, end: todayStr() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setData(null);
      setRange({ start: `${thisMonth()}-01`, end: todayStr() });
      setPickerOpen(true);
    } else {
      setPickerOpen(false);
    }
  }, [open]);

  const generate = async () => {
    if (!range.start || !range.end) { toast.error("Pick both a from and to date"); return; }
    if (range.start > range.end) { toast.error("From date must be before To date"); return; }
    setLoading(true);
    try {
      const r = await api.get(`/employees/${empId}/statement`, { params: range });
      setData(r.data);
      setPickerOpen(false);
    } catch {
      toast.error("Could not generate statement");
    } finally {
      setLoading(false);
    }
  };

  const shareText = () => {
    if (!data) return "";
    const e = data.employee;
    return `*Labour Statement — ${e.name}*\n${data.start || "start"} to ${data.end} · Balance: ₹${data.closing_balance}\n\n📎 Full statement attached as PDF`;
  };

  const exportExcel = () => {
    if (!data) return;
    const safe = data.employee.name.replace(/\s+/g, "_");
    downloadFile(`/employees/${empId}/statement/excel?start=${range.start}&end=${range.end}`, `statement_${safe}_${range.start}_to_${range.end}.xlsx`);
  };

  return (
    <>
      <Dialog open={pickerOpen} onOpenChange={(v) => { if (!v) { setPickerOpen(false); onOpenChange(false); } }}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader><DialogTitle>Generate Statement</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-500">Pick the period to include. Opening/closing balance accounts for everything before this range.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>From</Label>
                <Input data-testid="labour-stmt-start" type="date" className="font-data" value={range.start}
                  onChange={(e) => setRange({ ...range, start: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>To</Label>
                <Input data-testid="labour-stmt-end" type="date" className="font-data" value={range.end}
                  onChange={(e) => setRange({ ...range, end: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPickerOpen(false); onOpenChange(false); }}>Cancel</Button>
            <Button data-testid="btn-generate-labour-statement" className="bg-slate-900 hover:bg-slate-800" disabled={loading} onClick={generate}>
              {loading ? "Generating..." : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PrintableStatement open={!!data} onClose={() => { setData(null); onOpenChange(false); }} testid="labour-statement" shareText={shareText}
        onExcel={exportExcel} excelLabel="Export Excel"
        pdfFileName={data ? `statement_${data.employee.name.replace(/\s+/g, "_")}_${range.start}_to_${range.end}.pdf` : undefined}>
        {data && (
          <div className="font-data text-slate-800">
            <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-5">
              <div>
                <h1 className="font-sans text-2xl font-semibold tracking-tight">Labour Statement</h1>
                <p className="text-sm text-slate-500 mt-0.5">{data.employee.name} · {data.employee.gender === "M" ? "Male" : "Female"}</p>
              </div>
              <div className="text-right text-xs text-slate-500">Period<br /><span className="text-slate-800">{range.start} → {range.end}</span></div>
            </div>

            <div className="flex justify-between text-sm mb-5">
              <span className="text-slate-500">Opening balance: <span className="text-slate-800">{fmtMoney(data.opening_running)}</span></span>
              <span className="text-slate-500">Closing balance: <span className={`font-semibold ${balanceClass(data.closing_balance)}`}>{fmtMoney(data.closing_balance)}</span></span>
            </div>

            <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-slate-500 mb-2">Shift Summary</h2>
            <table className="w-full text-xs border border-slate-200 mb-5">
              <thead>
                <tr className="bg-slate-100 text-slate-500 font-sans">
                  <th className="px-2 py-1.5 text-left font-semibold">Shift</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Days</th>
                </tr>
              </thead>
              <tbody>
                {data.shift_rows.map((r) => (
                  <tr key={r.shift_id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 text-left">{r.name}</td>
                    <td className="px-2 py-1.5 text-right">{r.count}</td>
                  </tr>
                ))}
                {data.absent > 0 && (
                  <tr className="border-t border-slate-100">
                    <td className="px-2 py-1.5 text-left text-rose-600">Absent</td>
                    <td className="px-2 py-1.5 text-right">{data.absent}</td>
                  </tr>
                )}
                {data.shift_rows.length === 0 && data.absent === 0 && (
                  <tr><td colSpan={2} className="px-2 py-3 text-center text-slate-400">No attendance recorded in this period.</td></tr>
                )}
              </tbody>
            </table>

            <table className="w-full text-xs border border-slate-200">
              <thead>
                <tr className="bg-slate-100 text-slate-500 font-sans">
                  <th className="px-2 py-1.5 text-left font-semibold">Date</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Entry</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Amount</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((ev, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 text-left">{ev.date}</td>
                    <td className={`px-2 py-1.5 text-left capitalize ${evtStyle[ev.type] || ""}`}>{ev.detail}</td>
                    <td className={`px-2 py-1.5 text-right ${ev.amount < 0 ? "text-rose-600" : ev.amount > 0 ? "text-emerald-700" : "text-slate-400"}`}>{ev.amount === 0 ? "—" : fmtMoney(ev.amount)}</td>
                    <td className="px-2 py-1.5 text-right">{fmtMoney(ev.running)}</td>
                  </tr>
                ))}
                {data.events.length === 0 && <tr><td colSpan={4} className="px-2 py-3 text-center text-slate-400">No entries in this period.</td></tr>}
              </tbody>
            </table>

            {data.arecanut && data.arecanut.events.length > 0 && (
              <div className="mt-6">
                <div className="flex justify-between text-sm mb-2">
                  <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-emerald-700">Arecanut Season</h2>
                  <span className="text-slate-500">Balance: <span className={`font-semibold ${balanceClass(data.arecanut.closing_balance)}`}>{fmtMoney(data.arecanut.closing_balance)}</span></span>
                </div>
                <table className="w-full text-xs border border-slate-200">
                  <thead>
                    <tr className="bg-emerald-50 text-slate-500 font-sans">
                      <th className="px-2 py-1.5 text-left font-semibold">Date</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Entry</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Amount</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.arecanut.events.map((ev, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 text-left">{ev.date}</td>
                        <td className={`px-2 py-1.5 text-left capitalize ${evtStyle[ev.type] || ""}`}>{ev.detail}</td>
                        <td className={`px-2 py-1.5 text-right ${ev.amount < 0 ? "text-rose-600" : ev.amount > 0 ? "text-emerald-700" : "text-slate-400"}`}>{ev.amount === 0 ? "—" : fmtMoney(ev.amount)}</td>
                        <td className="px-2 py-1.5 text-right">{fmtMoney(ev.running)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-8 font-sans">Generated by AgriLedger · {new Date().toLocaleString("en-IN")}</p>
          </div>
        )}
      </PrintableStatement>
    </>
  );
}

// What a non-zero balance means, in words -- positive = we owe them.
function owedText(amount) {
  return amount > 0 ? `You owe ${fmtMoney(amount)}` : `Owes you ${fmtMoney(-amount)}`;
}

// Settles a labourer's account to zero by hand, e.g. someone who borrowed
// more than they earned, stopped coming, and paid it back. Records one
// "Account cleared" payment per ticked balance for exactly what's
// outstanding on the chosen date (server: POST /employees/{id}/clear-balance)
// -- history stays as it is, and deleting that payment on the Payments page
// undoes it. `only` = "arecanut"/"regular" starts with just that one ticked.
function ClearBalanceDialog({ empId, empName, open, onOpenChange, only }) {
  const [date, setDate] = useState(todayStr());
  const [note, setNote] = useState("");
  const [bal, setBal] = useState(null);
  const [picked, setPicked] = useState({ regular: true, arecanut: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(todayStr()); setNote("");
      setPicked({ regular: !only || only === "regular", arecanut: !only || only === "arecanut" });
    }
  }, [open, only]);
  useEffect(() => {
    if (!open || !empId || !date) return;
    setBal(null);
    api.get("/balances", { params: { as_of: date } })
      .then((r) => setBal(r.data[empId] || { regular: 0, arecanut: 0 }));
  }, [open, empId, date]);

  const lines = bal
    ? [["regular", "Regular", bal.regular], ["arecanut", "Arecanut", bal.arecanut]].filter(([, , v]) => Math.abs(v) >= 0.01)
    : [];
  const toClear = lines.filter(([key]) => picked[key]).map(([key]) => key);

  const clear = async () => {
    setSaving(true);
    try {
      await api.post(`/employees/${empId}/clear-balance`, { date, note, buckets: toClear });
      toast.success(`${empName || "Labourer"}'s ${toClear.length === 1 ? `${toClear[0]} balance` : "account"} cleared`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear balance");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]" data-testid="clear-balance-dialog">
        <DialogHeader><DialogTitle>Clear {empName || "labourer"}'s balance?</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
            {!bal && <div className="p-3 text-sm text-slate-400">Loading balance…</div>}
            {bal && lines.length === 0 && <div className="p-3 text-sm text-slate-500">Nothing to clear — balance is ₹0 on this date.</div>}
            {lines.map(([key, label, v]) => (
              <label key={key} className="flex items-center justify-between gap-3 p-3 text-sm cursor-pointer" data-testid={`clear-line-${key}`}>
                <span className="flex items-center gap-2 text-slate-600">
                  <Checkbox data-testid={`clear-pick-${key}`} checked={picked[key]}
                    onCheckedChange={(c) => setPicked((prev) => ({ ...prev, [key]: c === true }))} />
                  Clear {label.toLowerCase()} balance
                </span>
                <span className={`font-data font-semibold ${balanceClass(v)}`}>{owedText(v)}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            Marks each ticked balance as settled in full, so it becomes ₹0. Attendance, lends and payments stay on the statement.
            To undo, delete the "Account cleared" entry on the Payments page.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Settled on</Label>
              <Input data-testid="clear-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Input data-testid="clear-note" placeholder="Account cleared" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="btn-confirm-clear" className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            disabled={saving || toClear.length === 0} onClick={clear}>
            {saving ? "Clearing…" : toClear.length === 1 ? `Clear ${toClear[0] === "arecanut" ? "Arecanut" : "Regular"} Balance` : "Clear Account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
